<template>
  <div id="app">
    <header class="app-header left-rail" role="banner">
      <div class="header-content">
        <div class="vertical-brand" aria-label="One Million Interactive Pixel Canvas">
          <span class="brand-title title-gradient">One Million</span>
          <span class="brand-subtitle">Interactive Pixel Canvas</span>
        </div>
      </div>
    </header>

    <aside class="right-rail" role="complementary" aria-label="User activity">
      <div class="right-rail-content">
        <button
          class="rail-button rail-button--seo-toggle"
          type="button"
          @click="toggleSeoHero"
          :aria-pressed="!showSeoHero"
          aria-controls="seo-hero"
        >
          {{ showSeoHero ? 'Hide intro' : 'Show intro' }}
        </button>
        <a class="rail-button" href="/about/">About</a>
        <InfoBar :totalClients="totalClients" />
      </div>
    </aside>

    <main class="main-content" role="main">
      <section
        id="seo-hero"
        class="seo-hero"
        :class="{ 'seo-hero--collapsed': !showSeoHero }"
        aria-labelledby="seo-title canvas-heading"
      >
        <div class="seo-copy">
          <div class="seo-topline">
            <p class="eyebrow">Free collaborative pixel art</p>
          </div>
          <h1 id="seo-title" class="page-title">One Million Pixel Canvas</h1>
          <p class="page-summary">
            Draw together in real time on a massive shared pixel grid. Explore the canvas,
            collaborate with other users, and create visual stories that update instantly for
            everyone.
          </p>
        </div>

        <div class="seo-aside">
          <h2 id="canvas-heading" class="canvas-title">Interactive Canvas</h2>
          <p class="canvas-description">
            Navigate and click on pixels to contribute to the collaborative artwork. Use the sliders
            to explore different areas of the massive 1 million x 1 million pixel grid.
          </p>
          <ul class="seo-points" aria-label="Key features">
            <li>Real-time collaboration</li>
            <li>Free to use</li>
            <li>Works on desktop and mobile</li>
          </ul>
        </div>
      </section>

      <section class="canvas-container" aria-labelledby="canvas-heading">
        <div class="canvas-toolbar" role="region" aria-label="Cell image controls">
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            class="hidden-file-input"
            @change="onImageSelected"
          />
          <button
            type="button"
            class="upload-button"
            :disabled="isUploadingImage"
            @click="triggerImagePicker"
          >
            {{ isUploadingImage ? 'Uploading...' : 'Upload your image!' }}
          </button>
          <button
            v-if="pendingImageFile"
            type="button"
            class="upload-button upload-button--secondary"
            :disabled="isUploadingImage"
            @click="clearPendingImage"
          >
            Cancel
          </button>
          <span class="upload-status" :class="{ 'upload-status--error': uploadError }">
            {{ uploadStatusText }}
          </span>
        </div>
        <MAinTable
          :rows="rows"
          :posGlobalX="posGlobalX"
          :posGlobalY="posGlobalY"
          @cell-clicked="sendNotification"
          @currentPos-change="currentPosChange"
          @dimensions-changed="onDimensionsChanged"
          @scroll-by="scrollBy"
          role="application"
          aria-label="Interactive pixel canvas for collaborative art creation"
        />
      </section>
    </main>

    <!-- <footer class="app-footer" role="contentinfo">
      <div class="footer-content">
        <p>&copy; 2025 One Million Canvas. A collaborative art experiment.</p>
        <nav class="footer-nav" aria-label="Footer navigation">
          <a href="#about" class="footer-link">About</a>
          <a href="#privacy" class="footer-link">Privacy</a>
          <a href="#contact" class="footer-link">Contact</a>
        </nav>
      </div>
    </footer> -->

    <div
      v-if="activeModalImage"
      class="image-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Original image"
      @click.self="closeImageModal"
    >
      <div class="image-modal-card">
        <button type="button" class="image-modal-close" @click="closeImageModal">Close</button>
        <img class="image-modal-content" :src="activeModalImage" alt="Original cell image" />
      </div>
    </div>
  </div>
</template>

<script>
import InfoBar from './components/info-bar.vue';
import MAinTable from './components/main-table.vue';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Environment } from './environment.constant.js';

export default {
  name: 'App',
  components: {
    MAinTable,
    InfoBar,
  },
  data() {
    return {
      isAuthenticated: false,
      currentPos: 0,
      fingerprint: '',
      websocket: null,
      tokenJWT: '',
      rows: [],
      intervalId: null,
      posGlobalX: 0,
      posGlobalY: 0,
      totalClients: 0,
      showSeoHero: true,
      fieldWidth: Math.max(1, Math.floor((window.innerWidth - 180) / 22)),
      fieldHeight: Math.max(1, Math.floor((window.innerHeight - 220) / 22)),
      pendingImageFile: null,
      isUploadingImage: false,
      uploadError: '',
      activeModalImage: '',
      pendingUploadResolvers: {},
    };
  },
  computed: {
    uploadStatusText() {
      if (this.uploadError) return this.uploadError;
      if (this.pendingImageFile) {
        return `Image selected: ${this.pendingImageFile.name}. Click any target cell to upload or replace image.`;
      }
      return 'Click "Pick image", then click target cell to place it.';
    },
  },
  async mounted() {
    this.showSeoHero = window.sessionStorage.getItem('seoHeroHidden') !== '1';
    await this.getBrowserFingerprint();
    this.initWebSocket();
    window.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    if (this.websocket) {
      this.websocket.close();
    }
    window.removeEventListener('keydown', this.onKeydown);
  },

  methods: {
    normalizeCell(cell) {
      if (cell && typeof cell === 'object') {
        return {
          char: cell.char || '',
          hasImage: !!cell.hasImage,
          previewUrl: cell.previewUrl || null,
          originalUrl: cell.originalUrl || null,
        };
      }
      return { char: cell || '', hasImage: false, previewUrl: null, originalUrl: null };
    },
    toCellPayload(payload) {
      return this.normalizeCell({
        char: payload?.char || '',
        hasImage: payload?.hasImage,
        previewUrl: payload?.previewUrl,
        originalUrl: payload?.originalUrl,
      });
    },
    patchCellByPos(pos, cellPayload) {
      const posY = Math.floor((pos - this.currentPos) / Environment.TableWidth);
      const posX = pos - this.currentPos - posY * Environment.TableWidth;
      if (posX < 0 || posY < 0 || posX >= this.fieldWidth || posY >= this.fieldHeight) return;

      const newRows = [...this.rows];
      if (newRows[posY]) {
        newRows[posY] = [...newRows[posY]];
        newRows[posY][posX] = this.toCellPayload(cellPayload);
        this.rows = newRows;
      }
    },
    triggerImagePicker() {
      this.uploadError = '';
      this.$refs.imageInput?.click();
    },
    onImageSelected(event) {
      this.uploadError = '';
      const file = event?.target?.files?.[0];
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.uploadError = 'Only image files are allowed.';
        this.pendingImageFile = null;
        return;
      }
      this.pendingImageFile = file;
    },
    clearPendingImage() {
      this.pendingImageFile = null;
      this.uploadError = '';
      if (this.$refs.imageInput) this.$refs.imageInput.value = '';
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const value = String(reader.result || '');
          const splitIndex = value.indexOf(',');
          if (splitIndex < 0) {
            reject(new Error('Failed to parse image payload.'));
            return;
          }
          resolve(value.slice(splitIndex + 1));
        };
        reader.onerror = () => reject(new Error('Failed to read image file.'));
        reader.readAsDataURL(file);
      });
    },
    waitForImageUploadAck(requestId) {
      return new Promise((resolve, reject) => {
        this.pendingUploadResolvers[requestId] = { resolve, reject };
        setTimeout(() => {
          if (this.pendingUploadResolvers[requestId]) {
            delete this.pendingUploadResolvers[requestId];
            reject(new Error('Image upload timeout.'));
          }
        }, 15000);
      });
    },
    async uploadImageToCell(posClicked) {
      if (!this.pendingImageFile) return;
      this.isUploadingImage = true;
      this.uploadError = '';
      try {
        if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) {
          throw new Error('WebSocket is not connected.');
        }
        const base64Payload = await this.fileToBase64(this.pendingImageFile);
        const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const ackPromise = this.waitForImageUploadAck(requestId);
        this.sendWebsocket({
          event: 'cellImageUpload',
          data: {
            requestId,
            pos: posClicked,
            mimeType: this.pendingImageFile.type,
            fileBase64: base64Payload,
          },
        });
        const cell = await ackPromise;
        this.patchCellByPos(posClicked, cell);
        this.clearPendingImage();
      } catch (error) {
        this.uploadError = error?.message || 'Failed to upload image.';
      } finally {
        this.isUploadingImage = false;
      }
    },
    openImageModal(url) {
      const marker = url.includes('?') ? '&' : '?';
      this.activeModalImage = `${url}${marker}t=${Date.now()}`;
    },
    closeImageModal() {
      this.activeModalImage = '';
    },
    async initWebSocket() {
      //console.log("WebSocket подключение...");
      this.websocket = new WebSocket(process.env.VUE_APP_WEBSOCKET);
      // this.websocket = new WebSocket("wss://one-million.click/socket");
      // this.websocket = new WebSocket("wss://ws.postman-echo.com/raw");

      this.websocket.onopen = () => {
        this.sendWebsocket({ event: 'authentication', data: { fingerprint: this.fingerprint } });
      };

      this.websocket.onmessage = (event) => {
        const message = JSON.parse(event.data);
        if (message.event === 'authentication') {
          this.isAuthenticated = true;
          this.sendWebsocket({
            event: 'refreshCells',
            data: { pos: this.currentPos, width: this.fieldWidth, height: this.fieldHeight },
          });
        } else if (message.event === 'refreshCells') {
          this.rows = (message.data || []).map((row) =>
            (row || []).map((cell) => this.normalizeCell(cell))
          );
          this.posGlobalY = Math.floor(this.currentPos / Environment.TableWidth);
          this.posGlobalX = this.currentPos - this.posGlobalY * Environment.TableWidth;
        } else if (message.event === 'notify') {
          this.totalClients = message.data.totalClients;
        } else if (message.event === 'cellClick') {
          const data = message.data;
          this.patchCellByPos(data.pos, data);
        } else if (message.event === 'cellImageUpload') {
          const data = message.data;
          this.patchCellByPos(data.pos, data);
        } else if (message.event === 'cellImageUploadAck') {
          const requestId = message.requestId || message.data?.requestId;
          if (!requestId || !this.pendingUploadResolvers[requestId]) return;
          const pending = this.pendingUploadResolvers[requestId];
          delete this.pendingUploadResolvers[requestId];
          if (message.error) {
            pending.reject(new Error(message.error.message || 'Failed to upload image.'));
          } else {
            pending.resolve(message.data || null);
          }
        }
      };

      this.websocket.onerror = (error) => {
        console.error('WebSocket ошибка:', error);
      };

      this.websocket.onclose = () => {
        // console.log("WebSocket соединение закрыто");
      };
    },
    onDimensionsChanged({ cols, rows }) {
      const changed = cols !== this.fieldWidth || rows !== this.fieldHeight;
      this.fieldWidth = cols;
      this.fieldHeight = rows;
      if (changed && this.isAuthenticated) {
        this.sendWebsocket({
          event: 'refreshCells',
          data: { pos: this.currentPos, width: cols, height: rows },
        });
      }
    },
    currentPosChange(diff) {
      if (this.intervalId) clearInterval(this.intervalId);

      const dH = +diff.h - 50; // -50 … +50 (positive = right)
      const dV = -(+diff.v - 50); // -50 … +50 (positive = down)
      const magnitude = Math.max(Math.abs(dH), Math.abs(dV));

      if (magnitude < 3) return; // dead zone – slider at rest

      // Exponential speed curve: cells per tick (tick = 50 ms)
      // magnitude 10 → ~1 cell/tick, magnitude 30 → ~7, magnitude 50 → ~30
      const cellsPerTick = Math.max(1, Math.ceil(Math.pow(magnitude / 50, 2) * 30));
      const stepH = Math.round((dH / magnitude) * cellsPerTick);
      const stepV = Math.round((dV / magnitude) * cellsPerTick);

      this.intervalId = setInterval(() => {
        this.currentPos += stepH + stepV * Environment.TableWidth;
        this.currentPos = Math.max(
          0,
          Math.min(Environment.TableHeight * (Environment.TableWidth - 1), this.currentPos)
        );
        this.sendWebsocket({
          event: 'refreshCells',
          data: { pos: this.currentPos, width: this.fieldWidth, height: this.fieldHeight },
        });
      }, 50);
    },
    scrollBy({ dx, dy }) {
      this.currentPos += dx + dy * Environment.TableWidth;
      this.currentPos = Math.max(
        0,
        Math.min(Environment.TableHeight * (Environment.TableWidth - 1), this.currentPos)
      );
      if (this.isAuthenticated) {
        this.sendWebsocket({
          event: 'refreshCells',
          data: { pos: this.currentPos, width: this.fieldWidth, height: this.fieldHeight },
        });
      }
    },
    toggleSeoHero() {
      this.showSeoHero = !this.showSeoHero;
      window.sessionStorage.setItem('seoHeroHidden', this.showSeoHero ? '0' : '1');
    },
    async sendNotification(data) {
      const posClicked = this.currentPos + data.rowIndex * Environment.TableWidth + data.colIndex;
      const clickedCell = this.normalizeCell(data.cell);

      if (this.pendingImageFile) {
        await this.uploadImageToCell(posClicked);
        return;
      }

      if (clickedCell.hasImage && clickedCell.originalUrl) {
        this.openImageModal(clickedCell.originalUrl);
        return;
      }

      // console.log("Отправка данных по WebSocket:", data.rowIndex, data.colIndex, posClicked);
      this.sendWebsocket({
        event: 'cellClick',
        data: {
          pos: posClicked,
        },
      });
    },
    sendWebsocket(payload) {
      // console.log("Отправка данных по WebSocket:", payload);
      if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
        return this.websocket.send(JSON.stringify(payload));
      } else {
        console.error('WebSocket не открыт');
      }
    },
    async getBrowserFingerprint() {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      this.fingerprint = result.visitorId;
      // console.log("Browser Fingerprint:", result.visitorId);
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.activeModalImage) {
        e.preventDefault();
        this.closeImageModal();
        return;
      }
      if (!this.isAuthenticated) return;
      const mult = e.ctrlKey || e.metaKey ? 100 : e.shiftKey ? 10 : 1;
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          this.scrollBy({ dx: -mult, dy: 0 });
          break;
        case 'ArrowRight':
          e.preventDefault();
          this.scrollBy({ dx: mult, dy: 0 });
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.scrollBy({ dx: 0, dy: -mult });
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.scrollBy({ dx: 0, dy: mult });
          break;
        case 'PageUp':
          e.preventDefault();
          this.scrollBy({ dx: 0, dy: -this.fieldHeight });
          break;
        case 'PageDown':
          e.preventDefault();
          this.scrollBy({ dx: 0, dy: this.fieldHeight });
          break;
        case 'Home':
          e.preventDefault();
          this.scrollBy({ dx: -this.fieldWidth, dy: 0 });
          break;
        case 'End':
          e.preventDefault();
          this.scrollBy({ dx: this.fieldWidth, dy: 0 });
          break;
      }
    },
  },
};
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell,
    sans-serif;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  color: #2c3e50;
}

:root {
  --sidebar-width: clamp(64px, 8vw, 112px);
  --right-rail-width: clamp(56px, 6vw, 96px);
  --rail-gap: clamp(1mm, 0.5vw, 12px);
}

#app {
  height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding-left: var(--sidebar-width, 112px);
  padding-right: calc(var(--right-rail-width, 96px) + var(--rail-gap));
}

.app-header {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 100;
}

.app-header.left-rail {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: var(--sidebar-width);
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 2px 0 20px rgba(0, 0, 0, 0.1);
  padding: 0.75rem 0;
}

.header-content {
  height: 100%;
  padding: 0.5rem 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.vertical-brand {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  user-select: none;
}

.brand-title {
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
}

.brand-subtitle {
  font-size: 0.9rem;
  color: #6c757d;
  font-weight: 500;
}

.title-gradient {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.main-content {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: clamp(1mm, 0.8vw, 1.5rem);
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
  gap: 0.75rem;
}

.canvas-title {
  font-size: 1.5rem;
  color: #2d3748;
  margin: 0 0 1rem 0;
  font-weight: 600;
}

.canvas-description {
  margin-bottom: 1.5rem;
}

.canvas-description p {
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.6;
  margin: 0;
}

.seo-hero {
  margin-bottom: 0;
  padding: clamp(0.8rem, 0.9vw, 1rem) clamp(1rem, 1.35vw, 1.35rem);
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(17, 24, 39, 0.24), rgba(79, 70, 229, 0.22));
  border: 1px solid rgba(255, 255, 255, 0.16);
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(260px, 0.95fr);
  gap: 1rem;
  align-items: center;
  color: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.08);
}

.seo-copy,
.seo-aside {
  min-width: 0;
}

.seo-copy {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.seo-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.seo-aside {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.eyebrow {
  display: inline-block;
  margin-bottom: 0;
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.page-title {
  background: linear-gradient(
    90deg,
    #ff3b30 0%,
    #ff9500 16%,
    #ffcc00 32%,
    #34c759 48%,
    #00c7be 64%,
    #007aff 80%,
    #af52de 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  font-size: clamp(1.6rem, 2vw, 2.35rem);
  line-height: 1.1;
  margin: 0;
  font-weight: 800;
  text-shadow: none;
}

.page-summary {
  color: rgba(255, 255, 255, 0.88);
  font-size: 0.95rem;
  line-height: 1.55;
  margin: 0;
  max-width: none;
}

.seo-points {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  list-style: none;
  margin: 0;
  padding: 0;
  justify-content: flex-start;
}

.seo-points li {
  padding: 0.4rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.42);
  color: #1f2937;
  font-size: 0.88rem;
  font-weight: 600;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.seo-aside .canvas-title {
  font-size: 1.15rem;
  margin-bottom: 0;
  color: #ffffff;
}

.seo-aside .canvas-description {
  margin-bottom: 0;
}

.seo-aside .canvas-description,
.seo-aside .canvas-description p {
  color: rgba(255, 255, 255, 0.86);
}

.seo-hero--collapsed {
  padding-top: 0.55rem;
  padding-bottom: 0.55rem;
}

.seo-hero--collapsed .seo-copy {
  gap: 0.2rem;
}

.seo-hero--collapsed .page-summary,
.seo-hero--collapsed .seo-aside {
  display: none;
}

.seo-hero--collapsed .page-title {
  font-size: clamp(1.35rem, 1.65vw, 1.8rem);
}

.rail-button {
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 0.75rem;
  border: 1px solid rgba(16, 185, 129, 0.6);
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%);
  color: #0f5132;
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.2);
}

.rail-button--seo-toggle {
  border: 1px solid rgba(99, 102, 241, 0.32);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.16) 0%, rgba(79, 70, 229, 0.18) 100%);
  color: #1e1b4b;
  box-shadow: 0 2px 10px rgba(79, 70, 229, 0.16);
}

.rail-button--seo-toggle:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.22) 0%, rgba(79, 70, 229, 0.24) 100%);
}

.rail-button:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.28) 0%, rgba(5, 150, 105, 0.28) 100%);
}

.rail-button:focus-visible {
  outline: 2px solid rgba(16, 185, 129, 0.5);
  outline-offset: 2px;
}

.right-rail {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: var(--right-rail-width);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: -2px 0 20px rgba(0, 0, 0, 0.1);
  z-index: 100;
}

.right-rail-content {
  height: 100%;
  padding: 0.75rem 0.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.75rem;
}

.right-rail .info-bar {
  width: 100%;
  display: flex;
  justify-content: center;
}

.right-rail .users-counter {
  min-width: 0;
  width: 100%;
  padding: 0.75rem 0.5rem;
  border-radius: 12px;
  flex-direction: column;
  gap: 0.25rem;
  align-items: center;
}

.right-rail .counter-text {
  font-size: 1rem;
}

.right-rail .counter-label {
  font-size: 0.8rem;
}

.canvas-container {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: clamp(2mm, 1vw, 2rem);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.canvas-toolbar {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.8rem;
  flex-wrap: wrap;
  min-width: 0;
}

.hidden-file-input {
  display: none;
}

.upload-button {
  border: 1px solid rgba(59, 130, 246, 0.45);
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(37, 99, 235, 0.24));
  color: #0b3a7e;
  border-radius: 10px;
  padding: 0.45rem 0.8rem;
  font-weight: 600;
  cursor: pointer;
}

.upload-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.upload-button--secondary {
  border-color: rgba(148, 163, 184, 0.5);
  background: rgba(241, 245, 249, 0.9);
  color: #334155;
}

.upload-status {
  flex: 1 0 100%;
  max-width: 100%;
  min-width: 0;
  display: block;
  color: #334155;
  font-size: 0.9rem;
  line-height: 1.2;
}

.upload-status--error {
  color: #b91c1c;
}

.image-modal {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: 1rem;
}

.image-modal-card {
  width: min(92vw, 1100px);
  max-height: 92vh;
  background: #ffffff;
  border-radius: 14px;
  padding: 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.image-modal-close {
  align-self: flex-end;
  border: none;
  border-radius: 8px;
  background: #e2e8f0;
  color: #0f172a;
  font-weight: 600;
  padding: 0.4rem 0.75rem;
  cursor: pointer;
}

.image-modal-content {
  max-width: 100%;
  max-height: calc(92vh - 90px);
  object-fit: contain;
}

@media (max-width: 768px) {
  #app {
    padding-left: 0;
    padding-right: 0;
  }

  .app-header.left-rail {
    position: sticky;
    top: 0;
    left: 0;
    right: 0;
    bottom: auto;
    width: 100%;
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 2px 20px rgba(0, 0, 0, 0.1);
    padding: 0.75rem 0;
  }

  .header-content {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 1rem;
    height: auto;
  }

  .vertical-brand {
    writing-mode: initial;
    transform: none;
    gap: 0.25rem;
  }

  .brand-title {
    font-size: 1.25rem;
  }

  .main-content {
    padding: clamp(2mm, 2.5vw, 1rem);
  }

  .seo-hero {
    grid-template-columns: 1fr;
    row-gap: 0.75rem;
  }

  .seo-topline {
    align-items: flex-start;
  }

  .seo-points {
    justify-content: flex-start;
  }

  .description-card,
  .canvas-container {
    padding: clamp(2mm, 2.5vw, 1.5rem);
  }

  .right-rail {
    display: none;
  }
}

@media (max-width: 480px) {
  .title-gradient {
    font-size: 1.75rem;
  }

  .description-card,
  .canvas-container {
    padding: clamp(2mm, 3vw, 1rem);
  }
}
</style>
